"use client";

// Controles de accesibilidad del portal público:
//   - Tamaño de letra (A- / A / A+) — usa zoom CSS sobre el wrapper del
//     portal así escala todo (texto, imágenes, layout).
//   - Modo claro/oscuro — swap de CSS variables scopeadas a los data-attrs
//     del wrapper. No afecta el resto de la app (dashboard, superadmin).
//
// Persistencia en localStorage — los valores sobreviven navegación y sesión.
// El pre-hydration script del layout aplica los valores ANTES del primer
// paint para evitar flash de tema equivocado (FOUC).

import { useEffect, useState } from "react";
import { Sun, Moon } from "lucide-react";

type Theme = "light" | "dark";
type FontSize = "sm" | "md" | "lg";

const LS_THEME = "talenv_portal_theme";
const LS_FONT_SIZE = "talenv_portal_font_size";

function getPortalRoot(): HTMLElement | null {
  return document.querySelector<HTMLElement>("[data-portal-root]");
}

export function PortalThemeControls() {
  // Estados hidratan defaults; el useEffect lee localStorage al montar.
  // Renderizamos los defaults primero para evitar mismatch de hidratación
  // — el pre-hydration script del layout ya aplicó el estado correcto
  // al DOM antes de que React tome control.
  const [theme, setTheme] = useState<Theme>("light");
  const [fontSize, setFontSize] = useState<FontSize>("md");

  useEffect(() => {
    try {
      const t = (localStorage.getItem(LS_THEME) as Theme | null) ?? "light";
      const s = (localStorage.getItem(LS_FONT_SIZE) as FontSize | null) ?? "md";
      setTheme(t);
      setFontSize(s);
    } catch { /* localStorage puede fallar en modo privado */ }
  }, []);

  function applyTheme(next: Theme) {
    setTheme(next);
    try { localStorage.setItem(LS_THEME, next); } catch {}
    const root = getPortalRoot();
    if (root) root.dataset.portalTheme = next;
  }

  function applyFontSize(next: FontSize) {
    setFontSize(next);
    try { localStorage.setItem(LS_FONT_SIZE, next); } catch {}
    const root = getPortalRoot();
    if (root) root.dataset.portalFontSize = next;
  }

  return (
    <div className="flex items-center gap-2">
      {/* Font size — 3 tiers en segmented control */}
      <div
        className="flex items-center gap-0.5 rounded-lg bg-white/10 p-0.5"
        role="group"
        aria-label="Tamaño de letra"
      >
        <FontSizeBtn active={fontSize === "sm"} onClick={() => applyFontSize("sm")} label="Letra chica" text="A-" small />
        <FontSizeBtn active={fontSize === "md"} onClick={() => applyFontSize("md")} label="Letra mediana" text="A" />
        <FontSizeBtn active={fontSize === "lg"} onClick={() => applyFontSize("lg")} label="Letra grande" text="A+" large />
      </div>

      {/* Theme toggle */}
      <button
        type="button"
        onClick={() => applyTheme(theme === "light" ? "dark" : "light")}
        aria-label={theme === "light" ? "Activar modo oscuro" : "Activar modo claro"}
        title={theme === "light" ? "Modo oscuro" : "Modo claro"}
        className="h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
      >
        {theme === "light" ? <Moon size={14} /> : <Sun size={14} />}
      </button>
    </div>
  );
}

function FontSizeBtn({
  active,
  onClick,
  label,
  text,
  small,
  large,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  text: string;
  small?: boolean;
  large?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      title={label}
      className={`h-7 min-w-[24px] px-1.5 rounded-md font-semibold text-white transition-colors flex items-center justify-center ${
        active ? "bg-white/25" : "hover:bg-white/15"
      } ${small ? "text-[10px]" : large ? "text-sm" : "text-xs"}`}
    >
      {text}
    </button>
  );
}
